import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { inr } from "../lib/format";
import { Progress } from "./ui/progress";

export default function CartDrawer() {
  const { cartOpen, setCartOpen, cart, updateQty, removeItem, pricing, coupon, setCoupon } = useStore();
  const navigate = useNavigate();

  const subtotal = pricing?.subtotal ?? cart.reduce((a, b) => a + b.price * b.qty, 0);
  const threshold = pricing?.free_shipping_threshold ?? 1999;
  const remaining = Math.max(0, threshold - subtotal);
  const pct = Math.min(100, (subtotal / threshold) * 100);

  return (
    <>
      <div className={`fixed inset-0 bg-black/70 z-50 transition-opacity duration-300 ${cartOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setCartOpen(false)} />
      <aside
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-md bg-[#09090b] border-l border-zinc-800 flex flex-col transition-transform duration-300 ${cartOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h2 className="font-display text-2xl tracking-wide text-white">Your Bag ({cart.length})</h2>
          <button onClick={() => setCartOpen(false)} aria-label="Close" data-testid="cart-close-btn"><X className="text-zinc-300" /></button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8 gap-4">
            <p className="text-zinc-400">Your bag is empty.</p>
            <button onClick={() => { setCartOpen(false); navigate("/shop"); }}
              className="btn-accent px-6 py-3 text-xs font-bold uppercase tracking-wider">Shop Now</button>
          </div>
        ) : (
          <>
            <div className="px-5 pt-4">
              {remaining > 0 ? (
                <p className="text-xs text-zinc-400 mb-2">Add <span className="text-accent font-semibold">{inr(remaining)}</span> more for free shipping</p>
              ) : (
                <p className="text-xs text-accent mb-2 font-semibold">You've unlocked free shipping!</p>
              )}
              <Progress value={pct} className="h-1 bg-zinc-800" />
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {cart.map((it, i) => (
                <div key={i} className="flex gap-3" data-testid={`cart-item-${i}`}>
                  <img src={it.image} alt={it.name} className="w-20 h-24 object-cover bg-zinc-900" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <p className="text-sm font-semibold text-zinc-100 line-clamp-1">{it.name}</p>
                      <button onClick={() => removeItem(i)} aria-label="Remove" data-testid={`cart-remove-${i}`}>
                        <Trash2 size={15} className="text-zinc-500 hover:text-red-400" />
                      </button>
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5">{[it.size, it.color].filter(Boolean).join(" / ")}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-zinc-800">
                        <button onClick={() => updateQty(i, it.qty - 1)} className="px-2 py-1 text-zinc-300" aria-label="Decrease"><Minus size={13} /></button>
                        <span className="px-2 text-sm">{it.qty}</span>
                        <button onClick={() => updateQty(i, it.qty + 1)} className="px-2 py-1 text-zinc-300" aria-label="Increase"><Plus size={13} /></button>
                      </div>
                      <span className="text-sm font-semibold text-white">{inr(it.price * it.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-zinc-800 p-5 space-y-3">
              <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                placeholder="COUPON CODE" data-testid="cart-coupon-input"
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-sm uppercase tracking-wider placeholder:text-zinc-600 focus:border-accent-ss" />
              {pricing?.coupon_error && <p className="text-xs text-red-400">{pricing.coupon_error}</p>}
              {pricing?.coupon_code && <p className="text-xs text-accent">Coupon {pricing.coupon_code} applied</p>}
              <div className="flex justify-between text-sm text-zinc-300"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
              {pricing?.discount > 0 && <div className="flex justify-between text-sm text-accent"><span>Discount</span><span>-{inr(pricing.discount)}</span></div>}
              <div className="flex justify-between text-sm text-zinc-300"><span>Shipping</span><span>{pricing?.shipping ? inr(pricing.shipping) : "FREE"}</span></div>
              <div className="flex justify-between text-base font-bold text-white pt-2 border-t border-zinc-800">
                <span>Total</span><span data-testid="cart-total">{inr(pricing?.total ?? subtotal)}</span>
              </div>
              <button onClick={() => { setCartOpen(false); navigate("/checkout"); }}
                className="btn-accent w-full py-4 text-sm font-bold uppercase tracking-wider" data-testid="cart-checkout-btn">
                Checkout
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
